if (!customElements.get('gt-single-floating-cart')) {
  customElements.define('gt-single-floating-cart', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.quantity = this.querySelector('[data-floating-quantity]');
      this.minus = this.querySelector('[data-quantity-minus]');
      this.plus = this.querySelector('[data-quantity-plus]');
      this.add = this.querySelector('[data-floating-add]');
      this.addLabel = this.querySelector('[data-floating-add-label]');
      this.price = this.querySelector('[data-floating-price]');
      this.error = this.querySelector('[data-floating-error]');
      if (!this.root || !this.quantity || !this.add) return;
      this.rules = JSON.parse(this.querySelector('[data-floating-quantity-rules]').textContent);
      this.originalLabel = this.addLabel.textContent;
      this.originalBottom = getComputedStyle(document.body).paddingBottom;
      document.documentElement.style.setProperty('--gt-floating-cart-original-bottom', this.originalBottom);
      this.queueSync = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.sync(); });
      };
      this.onClick = event => {
        const button = event.target.closest('button');
        if (button === this.plus) this.changeQuantity(1);
        if (button === this.minus) this.changeQuantity(-1);
        if (button === this.add && this.mainButton && !this.add.disabled) {
          this.returnFocus = true;
          // Submit the current variant through Dawn's form, with the selected tier quantity.
          // App click handlers can otherwise retain a previously selected variant.
          this.setFormQuantity(this.currentQuantity());
          this.form.requestSubmit(this.mainButton);
          this.queueSync();
        }
      };
      this.onRootEvent = event => { if (!this.contains(event.target)) this.queueSync(); };
      this.addEventListener('click', this.onClick);
      this.root.addEventListener('click', this.onRootEvent);
      this.root.addEventListener('change', this.onRootEvent);
      this.root.addEventListener('input', this.onRootEvent);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queueSync();
      });
      this.observer.observe(this.root, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['class','disabled','aria-disabled','aria-checked','selected-variant-count','hidden','value']});
      this.overlayObserver = new MutationObserver(this.queueSync);
      this.overlayObserver.observe(document.body, {attributes:true,attributeFilter:['class']});
      document.querySelectorAll('cart-drawer,product-modal,quick-add-modal').forEach(element => {
        this.overlayObserver.observe(element, {attributes:true,attributeFilter:['class','open']});
      });
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--gt-floating-cart-height', Math.ceil(this.getBoundingClientRect().height) + 'px');
        this.queueSync();
      });
      this.resizeObserver.observe(this);
      window.addEventListener('resize', this.queueSync);
      this.sync();
    }
    bindForm() {
      this.form = document.getElementById(this.dataset.formId);
      const button = this.form?.querySelector('[type="submit"]');
      if (button !== this.mainButton) {
        this.buttonObserver?.disconnect();
        this.mainButton = button;
        if (button) {
          this.buttonObserver = new IntersectionObserver(this.queueSync, {threshold:0});
          this.buttonObserver.observe(button);
        }
      }
    }
    quantityRules() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      const rule = this.rules.find(item => String(item.id) === String(id)) || {};
      return {min:Number(rule.min) || 1,step:Number(rule.step) || 1,max:Number(rule.max) > 0 ? Number(rule.max) : Infinity};
    }
    appTiers() {
      return Array.from(this.root.querySelectorAll('.lb-qty-selector-tier-wrapper')).filter(tier => tier.getClientRects().length && getComputedStyle(tier).visibility !== 'hidden');
    }
    tierQuantity(tier) {
      if (!tier) return 0;
      const input = tier.querySelector('.lb-qty-selector-variant-quantity-input');
      if (input && Number(input.value) > 0) return Number(input.value);
      return Number(tier.querySelector('.lb-qty-selector-quantity-text')?.textContent.match(/\d+/)?.[0]) || 1;
    }
    mainQuantity() {
      return this.form?.elements.namedItem('quantity') || this.form?.querySelector('[name="quantity"]');
    }
    setFormQuantity(value) {
      let input = this.mainQuantity();
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.dataset.gtFloatingQuantity = 'true';
        this.form.appendChild(input);
      }
      input.value = String(value);
      return input;
    }
    currentQuantity() {
      const selected = this.appTiers().find(tier => tier.getAttribute('aria-checked') === 'true');
      return selected ? this.tierQuantity(selected) : Number(this.mainQuantity()?.value) || this.quantityRules().min;
    }
    changeQuantity(direction) {
      const rule = this.quantityRules();
      const current = this.currentQuantity();
      const target = Math.min(rule.max, Math.max(rule.min, current + direction * rule.step));
      if (target === current || !this.form) return;
      const tiers = this.appTiers();
      const selected = tiers.find(tier => tier.getAttribute('aria-checked') === 'true');
      if (tiers.length) {
        const targetTier = [...tiers].reverse().find(tier => {
          const minimum = Number(tier.querySelector('.lb-qty-selector-quantity-text')?.textContent.match(/\d+/)?.[0]) || 1;
          return minimum <= target;
        }) || tiers[0];
        if (targetTier !== selected) targetTier.click();
        else {
          const buttons = selected.querySelectorAll('.lb-qty-selector-variant-quantity-btn');
          const button = direction > 0 ? buttons[buttons.length - 1] : buttons[0];
          if (button && !button.disabled) button.click();
        }
      } else {
        const input = this.setFormQuantity(target);
        input.dispatchEvent(new Event('input', {bubbles:true}));
        input.dispatchEvent(new Event('change', {bubbles:true}));
      }
      this.queueSync();
    }
    sync() {
      this.bindForm();
      if (!this.mainButton) { this.setVisible(false); return; }
      const nativeQuantity = this.mainQuantity();
      if (nativeQuantity?.dataset.gtFloatingQuantity && this.appTiers().some(tier => tier.getAttribute('aria-checked') === 'true')) nativeQuantity.remove();
      const value = this.currentQuantity();
      const rule = this.quantityRules();
      const busy = this.mainButton.classList.contains('loading');
      const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      this.quantity.value = String(value);
      this.minus.disabled = disabled || value <= rule.min;
      this.plus.disabled = disabled || value + rule.step > rule.max;
      this.add.disabled = disabled;
      this.add.setAttribute('aria-busy', String(busy));
      this.add.classList.toggle('is-loading', busy);
      const label = this.mainButton.querySelector('span')?.textContent.trim() || this.originalLabel;
      if (this.addLabel.textContent !== label) this.addLabel.textContent = label;
      const price = this.root.querySelector('.gt-single-price');
      const source = price?.querySelector(price.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      const money = source?.textContent.trim().replace(/AU\$/g, '$').replace(/\s+AUD\b/g, '');
      if (money && this.price.textContent !== money) this.price.textContent = money;
      const errorWrapper = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorText = errorWrapper && !errorWrapper.hidden ? errorWrapper.querySelector('.product-form__error-message')?.textContent.trim() || '' : '';
      if (this.error.textContent !== errorText) this.error.textContent = errorText;
      this.error.hidden = !errorText;
      const cartOpen = document.querySelector('cart-drawer')?.classList.contains('active') || false;
      const overlayOpen = cartOpen || Boolean(document.querySelector('product-modal[open],quick-add-modal[open]')) || /(^|\s)overflow-hidden(?:-\S+)?(?=\s|$)/.test(document.body.className);
      const passedButton = this.mainButton.getBoundingClientRect().bottom <= 0;
      this.setVisible(passedButton && !overlayOpen);
      if (this.cartWasOpen && !cartOpen && this.returnFocus) {
        this.returnFocus = false;
        const focusTarget = this.classList.contains('is-visible') ? this.add : this.mainButton;
        requestAnimationFrame(() => { if (focusTarget.isConnected) focusTarget.focus({preventScroll:true}); });
      }
      this.cartWasOpen = cartOpen;
    }
    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.inert = !visible;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.root?.removeEventListener('click', this.onRootEvent);
      this.root?.removeEventListener('change', this.onRootEvent);
      this.root?.removeEventListener('input', this.onRootEvent);
      window.removeEventListener('resize', this.queueSync);
      if (!document.querySelector('gt-single-floating-cart')) {
        document.documentElement.style.removeProperty('--gt-floating-cart-height');
        document.documentElement.style.removeProperty('--gt-floating-cart-original-bottom');
      }
    }
  });
}
